import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Layout from './components/Layout';
import TeamQueue from './pages/TeamQueue';
import AccountDetail from './pages/AccountDetail';
import Tasks from './pages/Tasks';
import Dashboard from './pages/Dashboard';
import Reports from './pages/Reports';
import Admin from './pages/Admin';
import './App.css';

const ProtectedRoute = ({ children, requiredPermission }) => {
  const { user, loading, ...permissions } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission && !permissions[requiredPermission]) {
    return <Navigate to="/queue" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/queue" replace /> : <Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/queue" replace />} />
        <Route path="queue" element={<TeamQueue />} />
        <Route path="accounts/:accountId" element={<AccountDetail />} />
        <Route path="tasks" element={<Tasks />} />
        <Route
          path="dashboard"
          element={
            <ProtectedRoute requiredPermission="canViewDashboard">
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute requiredPermission="canViewReports">
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin"
          element={
            <ProtectedRoute requiredPermission="canManageUsers">
              <Admin />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/queue" replace />} />
    </Routes>
  );
}

const TOAST_STYLE = {
  background: '#18181B',
  border: '1px solid #27272A',
  color: '#E4E4E7',
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
            theme="dark"
            toastOptions={{ style: TOAST_STYLE }}
          />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
